/* Supply › Reports › Stock › Expiry Report
 * Items expired or expiring within a configurable window. */
import React, { useMemo, useState } from "react";
import { cbFmtINR, cbFmtNum, cbExpiryStatus } from "../supplyData";
import {
  Button,
  Pill,
  Select,
  Table,
  Tr,
  Td,
  SectionHeader,
  ToolbarBar,
  KpiBox,
  LoadingState,
  ErrorState,
  IconDownload,
} from "../supplyKit";
import { exportToXlsx } from "./reportUtils";
import { ReportBack, RackCell, useWarehouseLots } from "./reportShared";
import { PHARMACY_ROUTES } from "../../../Route/path";

const WINDOWS = [
  { id: "expired", label: "Already expired" },
  { id: "30", label: "Within 30 days" },
  { id: "60", label: "Within 60 days" },
  { id: "90", label: "Within 90 days" },
  { id: "180", label: "Within 180 days" },
];

const StockExpiry = () => {
  /* Store comes from the topbar switcher, app-wide. */
  const { wh, lots, loading, error, reload } = useWarehouseLots();
  const [window, setWindow] = useState("90");

  const rows = useMemo(() => {
    const out = lots
      .map((l) => ({ ...l, _st: cbExpiryStatus(l.exp) }))
      .filter((l) => (window === "expired" ? l._st.days < 0 : l._st.days < Number(window)))
      .sort((a, b) => String(a.exp).localeCompare(String(b.exp)));
    return out;
  }, [lots, window]);

  const totals = rows.reduce(
    (acc, l) => {
      acc.units += l.qtyA;
      acc.value += l.qtyA * l.mrp;
      if (l._st.days < 0) acc.expired += 1;
      return acc;
    },
    { units: 0, value: 0, expired: 0 }
  );

  const handleExport = () => {
    const headers = ["Medicine", "Code", "Batch", "Expiry", "Rack", "Days to expiry", "Qty available", "Value (MRP)", "Warehouse"];
    const body = rows.map((l) => [
      l.medicineName || "",
      l.medId || "",
      l.batch || "",
      l.exp || "",
      l.rackCode || "",
      l._st.days,
      l.qtyA,
      Number((l.qtyA * l.mrp).toFixed(2)),
      l.wh || "",
    ]);
    exportToXlsx(`expiry-report-${wh || "all"}.xlsx`, headers, body, "Expiry");
  };

  return (
    <div className="cb-fade-in" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <ReportBack to={PHARMACY_ROUTES.STOCK_REPORT} label="Stock Reports" />
      <SectionHeader
        title="Expiry Report"
        sub="Batches expired or about to expire, by warehouse"
        actions={
          <Button icon={<IconDownload size={14} />} onClick={handleExport} disabled={!rows.length}>
            Export
          </Button>
        }
      />

      <ToolbarBar>
        <Select value={window} onChange={(e) => setWindow(e.target.value)}>
          {WINDOWS.map((w) => (
            <option key={w.id} value={w.id}>
              {w.label}
            </option>
          ))}
        </Select>
        <div style={{ marginLeft: "auto", fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
          {cbFmtNum(rows.length)} batches
        </div>
      </ToolbarBar>

      <div className="grid grid-cols-1 sm:grid-cols-3" style={{ display: "grid", gap: 10 }}>
        <KpiBox label="Batches" value={cbFmtNum(rows.length)} variant="amber" />
        <KpiBox label="Already expired" value={cbFmtNum(totals.expired)} variant="red" />
        <KpiBox label="Value at risk (MRP)" value={cbFmtINR(totals.value)} variant="amber" />
      </div>

      {loading ? (
        <LoadingState label="Loading expiry report…" />
      ) : error ? (
        <ErrorState message={error} onRetry={reload} />
      ) : (
        <Table
          columns={[
            { label: "Medicine" },
            { label: "Code" },
            { label: "Batch" },
            { label: "Expiry" },
            { label: "Rack" },
            { label: "Days", align: "right" },
            { label: "Qty available", align: "right" },
            { label: "Value at MRP", align: "right" },
            { label: "Status" },
          ]}
        >
          {rows.length === 0 && (
            <tr>
              <Td colSpan={9} style={{ textAlign: "center", color: "var(--fg-muted)", height: 80 }}>
                No batches in this window.
              </Td>
            </tr>
          )}
          {rows.map((l) => (
            <Tr key={l.id}>
              <Td style={{ fontWeight: 600, color: "var(--fg-strong)" }}>{l.medicineName}</Td>
              <Td mono>{l.medId}</Td>
              <Td mono>{l.batch}</Td>
              <Td mono>{l.exp}</Td>
              {/* Where to go and pull it off the shelf. */}
              <RackCell lot={l} />
              <Td mono right style={{ color: l._st.days < 0 ? "var(--red-700)" : "var(--amber-700)", fontWeight: 600 }}>
                {l._st.days < 0 ? `${Math.abs(l._st.days)}d ago` : `${l._st.days}d`}
              </Td>
              <Td mono right>
                {cbFmtNum(l.qtyA)}
              </Td>
              <Td mono right style={{ fontWeight: 600 }}>
                {cbFmtINR(l.qtyA * l.mrp)}
              </Td>
              <Td>
                <Pill variant={l._st.status} dot>
                  {l._st.label}
                </Pill>
              </Td>
            </Tr>
          ))}
        </Table>
      )}
    </div>
  );
};

export default StockExpiry;
